import { useCallback, useEffect, useState } from 'react';

/**
 * Recursively assigns missing properties on 'value' from 'defaultValue'
 */
const assignDefaults = (value: any, defaultValue: any) => {
  if (defaultValue === undefined) return value;

  const result = value;
  Object.keys(defaultValue).forEach((varType) => {
    const dValue = defaultValue[varType];
    const existingValue = value[varType];
    if (existingValue === undefined) {
      result[varType] = dValue;
    } else if (existingValue instanceof Object) {
      result[varType] = assignDefaults(existingValue, dValue);
    }
  });
  return result;
};

/**
 * A hook to store values in local storage.
 * Functions essentially like useState.
 * Includes event monitoring to detect if changed in another tab.
 * @param key The key to store the value under
 * @param initialValue The initial value to use if no stored value exists.
 * @returns The current stored value and a function to update it.
 */
const useLocalStorage = <T>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] => {
  const loadStoredValue = useCallback((storeKey: string): T => {
    // Get from local storage by storeKey
    const item = localStorage.getItem(storeKey);

    // Check if item exists
    if (item) {
      // Check if the initial is a type of object or is undefined and as such can be spread
      if (initialValue instanceof Object || initialValue === undefined) {
        const parsed = JSON.parse(item);
        const result = assignDefaults(parsed, initialValue);
        // Return parsed value and spread initial value over it, to fill missing properties
        return result as T;
      }
      // Basic parse if initial is not an object; string, number etc..
      return JSON.parse(item) as T;
    }
    return initialValue;
  }, [initialValue]);
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      return loadStoredValue(key);
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  const checkStorage = useCallback(
    (e: StorageEvent) => {
      if (e.key === key) {
        const value = loadStoredValue(key);
        setStoredValue(value);
        console.info('Local storage was changed on another page');
      }
    },
    [key, loadStoredValue],
  );
  useEffect(() => {
    window.addEventListener('storage', checkStorage);
    return () => window.removeEventListener('storage', checkStorage);
  }, [checkStorage]);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);

      if (valueToStore === undefined) {
        localStorage.removeItem(key);
      } else {
        // Save to local storage
        localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue ?? initialValue, setValue];
};

export default useLocalStorage;
